import { Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useTranslation';

// Configurable Wealthify link
const WEALTHIFY_URL = 'https://wealthify.id';

export default function Footer() {
  const { language, getRoute } = useLanguage();
  const { t, isEnglish } = useTranslation();
  
  // Bilingual calculator links
  const calculatorLinks = isEnglish ? [
    { path: getRoute('investasi'), label: 'Investment Calculator' },
    { path: getRoute('aibudget'), label: 'AI Budget Planner' },
    { path: getRoute('danadarurat'), label: 'Emergency Fund' },
    { path: getRoute('kpr'), label: 'Mortgage Simulator' },
    { path: getRoute('asuransi'), label: 'Insurance' },
  ] : [
    { path: getRoute('investasi'), label: 'Kalkulator Investasi' },
    { path: getRoute('aibudget'), label: 'AI Budget Planner' },
    { path: getRoute('danadarurat'), label: 'Dana Darurat' },
    { path: getRoute('kpr'), label: 'Simulasi KPR' },
    { path: getRoute('asuransi'), label: 'Asuransi' },
  ];
  
  // Bilingual education/about links
  const aboutLinks = isEnglish ? [
    { path: getRoute('news'), label: 'Financial Articles' },
    { path: '/en/about', label: 'About Us' },
    { path: '/en/contact', label: 'Contact Us' },
  ] : [
    { path: getRoute('news'), label: 'Artikel Keuangan' },
    { path: '/tentang', label: 'Tentang Kami' },
    { path: '/kontak', label: 'Hubungi Kami' },
  ];
  
  // Bilingual legal links
  const legalLinks = isEnglish ? [
    { path: '/en/privacy', label: 'Privacy Policy' },
    { path: '/en/disclaimer', label: 'Disclaimer' },
    { path: '/en/contact', label: 'Contact' },
    { path: '/en/about', label: 'About' },
  ] : [
    { path: '/privasi', label: 'Kebijakan Privasi' },
    { path: '/disclaimer', label: 'Disclaimer' },
    { path: '/kontak', label: 'Kontak' },
    { path: '/tentang', label: 'Tentang' },
  ];
  
  return (
    <footer className="bg-white border-t border-border mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <h3 className="text-lg font-heading font-bold text-primary mb-3">KalkulatorDuit</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {isEnglish 
                ? 'Free AI-powered financial planning platform to help you plan investments, emergency funds, mortgages, and insurance needs more effectively.'
                : 'Platform kalkulator keuangan gratis untuk membantu Anda merencanakan investasi, dana darurat, KPR, dan kebutuhan asuransi dengan lebih baik.'
              }
            </p>
          </div>
          
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">
              {isEnglish ? 'Calculators' : 'Kalkulator'}
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {calculatorLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">
              {isEnglish ? 'Resources' : 'Edukasi'}
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {aboutLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="border-t border-border mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              {legalLinks.map((link, index) => (
                <span key={link.path} className="flex items-center gap-4">
                  <Link to={link.path} className="hover:text-primary transition-colors">
                    {link.label}
                  </Link>
                  {index < legalLinks.length - 1 && <span className="text-border">|</span>}
                </span>
              ))}
            </div>
            
            {/* Wealthify Branding - Non-clickable */}
            <div className="flex items-center gap-3 px-5 py-3 bg-secondary/50 rounded-xl">
              <span className="text-sm text-muted-foreground font-medium">Powered by</span>
              <img 
                src="/images/logos/wealthify-logo.webp" 
                alt="Wealthify - Platform Edukasi Keuangan Indonesia"
                width="140"
                height="36"
                className="h-9 w-auto object-contain"
                loading="lazy"
              />
            </div>
          </div>
          
          {/* Copyright */}
          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground mb-1">
              {isEnglish 
                ? <>Made with <Heart className="inline w-4 h-4 text-red-500 fill-current" /> for Indonesia</>
                : <>Dibuat dengan <Heart className="inline w-4 h-4 text-red-500 fill-current" /> untuk Indonesia</>
              }
            </p>
            <p className="text-xs text-muted-foreground">
              {isEnglish 
                ? '© 2026 KalkulatorDuit.id - AI Financial Planning Platform'
                : '© 2026 KalkulatorDuit.id - Platform Edukasi Keuangan'
              }
            </p>
          </div>
          
          {/* Partner Badge - Seedbacklink Verification */}
          <div className="text-center mt-3">
            <a 
              href="https://id.seedbacklink.com/" 
              target="_blank" 
              rel="nofollow sponsored noopener"
              aria-label="Seedbacklink Partner"
              className="inline-block opacity-60 hover:opacity-80 transition-opacity"
              style={{ fontSize: '11px' }}
            >
              <img 
                src="https://id.seedbacklink.com/wp-content/uploads/2025/04/badge-seedbacklink-media.png"
                alt="Seedbacklink"
                title="Seedbacklink"
                width="80"
                height="auto"
                className="inline-block"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}